// packages/components/src/showcase/ShowcaseHero.tsx
import React from 'react';
import { Button, Badge } from '@dopomogai/ui';
import { Sparkles, MessageSquare, PlayCircle } from 'lucide-react';

export interface ShowcaseHeroProps {
  visual?: React.ReactNode;
  title: string;
  summary?: string;
  badges?: string[];
  primaryCta: {
    text: string;
    onClick: () => void;
    ariaLabel?: string;
  };
  secondaryCta?: {
    text: string;
    onClick: () => void;
    ariaLabel?: string;
  };
  investCta?: {
    text: string;
    onClick: () => void;
    ariaLabel?: string;
    visible?: boolean;
  };
  chatHint?: string;
  className?: string;
}

export function ShowcaseHero({
  visual,
  title,
  summary,
  badges = [],
  primaryCta,
  secondaryCta,
  investCta,
  chatHint = "Interview me below — I'll help you pick a bundle.",
  className,
}: ShowcaseHeroProps) {
  // If no visual is provided, render a clean single-column header (no left pane gap)
  if (!visual) {
    return (
      <section className={['bg-white border-2 border-black rounded-xl overflow-hidden', className || ''].join(' ')}>
        <div className="p-6 md:p-10 lg:p-12">
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight">{title}</h1>
            <Badge text="Agent" accentColor="green" />
          </div>
          {summary && <p className="text-lg md:text-xl text-slate-600 mt-3">{summary}</p>}
          {badges.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {badges.map((b, i) => (
                <Badge key={`${b}-${i}`} text={b} accentColor="gray" />
              ))}
            </div>
          )}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              variant="primary"
              size="lg"
              onClick={primaryCta.onClick}
              aria-label={primaryCta.ariaLabel || 'Customize and get a quote'}
              icon={<Sparkles size={18} />}
              className="!py-3.5"
              text={primaryCta.text}
            />
            {secondaryCta && (
              <Button
                variant="secondary"
                size="lg"
                onClick={secondaryCta.onClick}
                aria-label={secondaryCta.ariaLabel || 'Book a discovery call'}
                className="!py-3.5"
                text={secondaryCta.text}
              />
            )}
            {investCta?.visible && (
              <Button
                variant="outline"
                size="lg"
                onClick={investCta.onClick}
                aria-label={investCta.ariaLabel || 'Invest'}
                icon={<PlayCircle size={18} />}
                className="!py-3.5"
                text={investCta.text}
              />
            )}
          </div>
          {chatHint && (
            <div className="mt-3 inline-flex items-center gap-2 text-sm text-slate-600">
              <MessageSquare size={16} className="text-emerald-600" />
              {chatHint}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className={['bg-white border-2 border-black rounded-xl overflow-hidden', className || ''].join(' ')}>
      <div className="flex flex-col lg:flex-row">
        {/* Left column - Visual */}
        <div className="lg:w-1/2">
          <div className="p-6 md:p-8 lg:p-10">
            {visual}
          </div>
        </div>

        {/* Right column - Content */}
        <div className="lg:w-1/2 p-6 md:p-8 lg:p-10">
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight">{title}</h1>
            <Badge text="Agent" accentColor="green" />
          </div>
          {summary && <p className="text-lg md:text-xl text-slate-600 mt-3">{summary}</p>}
          {badges.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {badges.map((b, i) => (
                <Badge key={`${b}-${i}`} text={b} accentColor="gray" />
              ))}
            </div>
          )}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              variant="primary"
              size="lg"
              onClick={primaryCta.onClick}
              aria-label={primaryCta.ariaLabel || 'Customize and get a quote'}
              icon={<Sparkles size={18} />}
              className="!py-3.5"
              text={primaryCta.text}
            />
            {secondaryCta && (
              <Button
                variant="secondary"
                size="lg"
                onClick={secondaryCta.onClick}
                aria-label={secondaryCta.ariaLabel || 'Book a discovery call'}
                className="!py-3.5"
                text={secondaryCta.text}
              />
            )}
            {investCta?.visible && (
              <Button
                variant="outline"
                size="lg"
                onClick={investCta.onClick}
                aria-label={investCta.ariaLabel || 'Invest'}
                icon={<PlayCircle size={18} />}
                className="!py-3.5"
                text={investCta.text}
              />
            )}
          </div>
          {chatHint && (
            <div className="mt-3 inline-flex items-center gap-2 text-sm text-slate-600">
              <MessageSquare size={16} className="text-emerald-600" />
              {chatHint}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default ShowcaseHero;
